import { useState, useEffect } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Breadcrumb,
    Modal,
} from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import dltWhiteIcon from "assets/svg/delete-white.svg";
import EventVariableService from "service/eventVariableService";
import {
    HttpStatus,
    TypeOption,
    Direction,
    Patterns,
    MAX_TERMINAL_VALUE,
    MIN_TERMINAL_VALUE,
    OVERFLOW_DEFAULT,
    STEP_DEFAULT,
    EVENT_VARIABLE_TIMER_FORMAT,
    FETCH_EVENT_VARIABLES_FAIL_MESSAGE,
    FETCH_EVENTS_FAIL_MESSAGE,
    INVALID_OPERATION_MESSAGE,
    INVALID_EVENT_VARIABLE_NAME_MESSAGE,
} from "constant";
import { convertSecondsToTimeString, isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import DefaultModal from "components/modals/ModalTemplate";
import eventService from "service/eventService";
import {
    getInitialValue,
    validateEventVariableValue,
    validateLimitError,
    validateStepSizeValue,
    validateTerminalValue,
} from "utils/eventFunctions";
import { VariablePatch, Variable, Event } from "generated/models/Event";
import { get, uniqBy } from "lodash";
import { canAccess } from "utils/authorize-action";
import ConditionCol from "components/events/EventList/ConditionCol";
import ActionCol from "components/events/EventList/ActionCol";
import { EventVariableLimit } from "constant/event";

type IParams = {
    eventVariableId: string;
};

const EventVariableDetail = () => {
    const { eventVariableId }: IParams = useParams();
    const history = useHistory();

    const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [eventVariable, setEventVariable] = useState<Variable>(
        {} as Variable
    );
    const [eventVariableName, setEventVariableName] = useState<string>("");
    const [eventVariableType, setEventVariableType] = useState<TypeOption>(
        TypeOption.Boolean
    );
    const [eventVariableValue, setEventVariableValue] = useState({
        [TypeOption.Boolean]: "true",
        [TypeOption.Integer]: "0",
        [TypeOption.Float]: "0.00",
        [TypeOption.Timer]: "00:00:00",
        [TypeOption.Counter]: "0",
    });
    const [direction, setDirection] = useState(Direction.Up);
    const [overflow, setOverflow] = useState(true);
    const [terminal, setTerminal] = useState(MAX_TERMINAL_VALUE);
    const [step, setStep] = useState("1");
    const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
    const [associatedEvents, setAssociatedEvents] = useState<Event[]>([]);
    const [eventVariableValueError, setEventVariableValueError] = useState("");
    const [terminalValueError, setTerminalValueError] = useState("");
    const [stepSizeValueError, setStepSizeValueError] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [variableInfo, setVariableInfo] = useState<Variable[] | any[]>([]);
    const [isShowResetModal, setIsShowResetModal] = useState(false);
    const [isShowEditModal, setIsShowEditModal] = useState(false);
    const [upperLimit, setUpperLimit] = useState(
        String(EventVariableLimit.MAX)
    );
    const [lowerLimit, setLowerLimit] = useState(
        String(EventVariableLimit.MIN)
    );
    const [upperLimitError, setUpperLimitError] = useState("");
    const [lowerLimitError, setLowerLimitError] = useState("");

    const resetErrors = () => {
        setEventVariableValueError("");
        setTerminalValueError("");
        setStepSizeValueError("");
        setUpperLimitError("");
        setLowerLimitError("");
    };

    useEffect(() => {
        if (isShowEditModal) {
            resetErrors();
        }
    }, [isShowEditModal]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateLimitError = (
        initialValue: string,
        upperLimit: string,
        lowerLimit: string
    ) => {
        const { upperLimitErrorMsg, lowerLimitErrorMsg } = validateLimitError(
            initialValue,
            upperLimit,
            lowerLimit,
            eventVariableType
        );

        setUpperLimitError(upperLimitErrorMsg);
        setLowerLimitError(lowerLimitErrorMsg);
    };

    const getAssociatedEvents = (variable: Variable) => {
        const in_actions = get(variable, "in_actions", []);
        const in_conditions = get(variable, "in_conditions", []);
        return uniqBy([...in_actions, ...in_conditions], "uuid");
    };

    const formatEventVariableValue = (type: string, value: string) => {
        switch (type) {
            case TypeOption.Float:
                return Number(value).toFixed(2);
            case TypeOption.Timer:
                return convertSecondsToTimeString(
                    Number(value),
                    EVENT_VARIABLE_TIMER_FORMAT
                );
            case TypeOption.Counter:
            case TypeOption.Integer:
                return Number(value);
            default:
                return value;
        }
    };

    const fetchData = async () => {
        setIsPageLoading(true);

        if (!canAccess("event:read")) {
            setIsPageLoading(false);
            setIsForbiddenResource(true);
            return;
        }

        const [respEventVariable, respEvent] = await Promise.all([
            EventVariableService.getEventVariableById(eventVariableId),
            eventService.readEvent(),
        ]);

        if (
            respEvent.status === HttpStatus.FORBIDDEN ||
            respEventVariable.status === HttpStatus.FORBIDDEN
        ) {
            setIsForbiddenResource(true);
            setIsPageLoading(false);
            return;
        }

        if (
            !isHttpSuccess(respEventVariable.status) ||
            !isHttpSuccess(respEvent.status)
        ) {
            if (!isHttpSuccess(respEventVariable.status)) {
                showErrorAlert(
                    getAPIError(
                        respEventVariable,
                        FETCH_EVENT_VARIABLES_FAIL_MESSAGE
                    )
                );
            }

            if (!isHttpSuccess(respEvent.status)) {
                showErrorAlert(
                    getAPIError(respEvent, FETCH_EVENTS_FAIL_MESSAGE)
                );
            }

            setIsPageLoading(false);
            return;
        }

        setAssociatedEvents(getAssociatedEvents(respEventVariable.data));
        let { name, type_, initial, attributes, current } =
            respEventVariable.data;
        setEventVariable(respEventVariable.data);
        setEventVariableName(name);
        setEventVariableType(type_);
        setEventVariableValue({
            ...eventVariableValue,
            [type_]: formatEventVariableValue(type_, initial),
        });

        const eventVariableInfo = [
            {
                label: "Event Variable Name",
                value: name,
                className: "text-primary-blue-4",
            },
            {
                label: "Type",
                value: type_.toLowerCase().capitalize(),
                className: "text-primary-blue-4",
            },
        ];
        const directionValue = get(attributes, "direction", true)
            ? "Up count"
            : "Down count";

        if (type_ === TypeOption.Counter) {
            setDirection(
                get(attributes, "direction", true)
                    ? Direction.Up
                    : Direction.Down
            );
            setTerminal(get(attributes, "terminal", MIN_TERMINAL_VALUE));
            setStep(get(attributes, "step", STEP_DEFAULT));
            setOverflow(get(attributes, "overflow", OVERFLOW_DEFAULT));

            eventVariableInfo.push({
                label: "Count",
                value: directionValue,
                className: "text-primary-blue-4",
            });
        }

        eventVariableInfo.push(
            {
                label: "Current Value",
                value: formatEventVariableValue(type_, current + "")
                    .toString()
                    .capitalize(),
                className: "text-primary-green-1",
            },
            {
                label: "Initial Value",
                value: formatEventVariableValue(type_, initial)
                    .toString()
                    .capitalize(),
                className: "text-primary-blue-4",
            }
        );

        if (type_ === TypeOption.Counter) {
            eventVariableInfo.push(
                {
                    label: "Terminal Value",
                    value: get(attributes, "terminal", MIN_TERMINAL_VALUE),
                    className: "text-primary-blue-4",
                },
                {
                    label: "Step size",
                    value: (
                        <>
                            {get(attributes, "step", STEP_DEFAULT)}{" "}
                            <span className="wrap-around-hint ml-2">
                                (The absolute difference between the Initial
                                Value and Terminal Value must be a multiple
                                of Step size.)
                            </span>
                        </>
                    ),
                    className: "text-primary-blue-4",
                },
                {
                    label: "Wraparound",
                    value: (
                        <div className="wrap-around-col disabled-custom-switch">
                            <Form.Check
                                custom
                                name="wraparound"
                                aria-label="wraparound"
                                type="switch"
                                checked={get(
                                    attributes,
                                    "overflow",
                                    OVERFLOW_DEFAULT
                                )}
                                disabled={true}
                                label={
                                    <span className="wrap-around-hint">
                                        (If enabled : Terminal value + Step
                                        size = Initial Value, if disabled:
                                        Terminal value + Step size =
                                        Terminal value)
                                    </span>
                                }
                            />
                        </div>
                    ),
                    className: "text-primary-blue-4",
                },
            );
        }

        if ([TypeOption.Integer, TypeOption.Float].includes(type_)) {
            let upper_limit = get(
                attributes,
                "upper_limit",
                EventVariableLimit.MAX
            );
            let lower_limit = get(
                attributes,
                "lower_limit",
                EventVariableLimit.MIN
            );
            if (type_ === TypeOption.Float) {
                upper_limit = Number(upper_limit).toFixed(2);
                lower_limit = Number(lower_limit).toFixed(2);
            }
            setUpperLimit(upper_limit);
            setLowerLimit(lower_limit);

            eventVariableInfo.push(
                {
                    label: "Upper Limit",
                    value: upper_limit,
                    className: "text-primary-blue-4",
                },
                {
                    label: "Lower Limit",
                    value: lower_limit,
                    className: "text-primary-blue-4",
                },
            );
        }

        setVariableInfo(eventVariableInfo);
        setIsPageLoading(false);
    };

    const renderAssociatedEvents = () => {
        if (!associatedEvents.length) {
            return (
                <div className="w-100 mt-4 text-center justify-content-center">
                    No Associated Events to display
                </div>
            );
        }
        return associatedEvents.map((associatedEvent) => {
            return (
                <div className="table-row" key={associatedEvent.uuid}>
                    <Row className="no-checkbox">
                        <Col md={{ span: 4, offset: 0 }} className="pl-4">
                            <Link
                                to={{
                                    pathname: `/event-details/${associatedEvent.uuid}`,
                                }}
                            >
                                <span>{associatedEvent.name}</span>
                            </Link>
                        </Col>

                        <Col md={{ span: 3, offset: 0 }}>
                            <ConditionCol
                                conditions={get(
                                    associatedEvent,
                                    "conditions",
                                    []
                                )}
                            />
                        </Col>

                        <Col md={{ span: 3, offset: 0 }}>
                            <ActionCol
                                actions={get(associatedEvent, "actions", [])}
                            />
                        </Col>

                        <Col md={{ span: 1, offset: 0 }}>
                            <HoverAuthorizeTooltip permission="event:update">
                                <span className="d-inline-block">
                                    <Form.Check
                                        disabled={true}
                                        type="switch"
                                        id={`switch_${associatedEvent.uuid}`}
                                        label=""
                                        checked={associatedEvent.activate}
                                    />
                                </span>
                            </HoverAuthorizeTooltip>
                        </Col>

                        <Col md={{ span: 1, offset: 0 }}>
                            <Link
                                to={{
                                    pathname: `/event-details/${associatedEvent.uuid}`,
                                }}
                            >
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Link>
                        </Col>
                    </Row>
                </div>
            );
        });
    };

    const updateEventVariable = async (e: any) => {
        e.preventDefault();
        setIsUpdating(true);
        const initialValue = getInitialValue(
            eventVariableType,
            eventVariableValue[eventVariableType]
        );

        const payload: VariablePatch = {
            name: eventVariableName.trim(),
            type_: eventVariableType,
            initial: initialValue,
        };

        if (eventVariableType === TypeOption.Counter) {
            payload.attributes = {
                terminal: +terminal,
                step: +step,
                direction: Boolean(direction),
                overflow,
            };
        }

        if (
            [TypeOption.Float, TypeOption.Integer].includes(eventVariableType)
        ) {
            payload.attributes = {
                upper_limit: +upperLimit,
                lower_limit: +lowerLimit,
            };
        }

        const updatedResp = await EventVariableService.updateEventVariable(
            eventVariableId,
            payload
        );

        if (isHttpSuccess(updatedResp.status)) {
            await fetchData();
            showSuccessAlert({
                message: "Event variable has been updated successfully.",
            });
        } else {
            const error = getAPIError(updatedResp, INVALID_OPERATION_MESSAGE);
            if (error.title === "Unable to edit Event Variable") {
                error.title = "Error";
                error.message =
                    "This Event Variable cannot be edited. Please disable all related Events before editing it.";
            }
            showErrorAlert(error);
        }
        setIsUpdating(false);
    };

    const handleDeleteEventVariable = async () => {
        const deleteResp = await EventVariableService.deleteEventVariableById(
            eventVariableId
        );
        if (isHttpSuccess(deleteResp.status)) {
            showSuccessAlert({
                message: "Event variable has been deleted successfully.",
            });
            history.push("/events?activeKey=event-variables");
        } else {
            showErrorAlert(
                getAPIError(
                    deleteResp,
                    `Unable to delete event variable ${eventVariableName}`
                )
            );
        }
        setIsShowDeleteModal(false);
    };

    const handleEventVariableName = (value: string) => {
        setEventVariableName(value);
    };

    const handleEventVariableValue = (value: string) => {
        setEventVariableValue({
            ...eventVariableValue,
            [eventVariableType]: value,
        });

        const errorMsg = validateEventVariableValue(eventVariableType, value);
        setEventVariableValueError(errorMsg);
        if (
            eventVariableType === TypeOption.Integer ||
            eventVariableType === TypeOption.Float
        ) {
            updateLimitError(value, upperLimit, lowerLimit);
        }
        if (eventVariableType === TypeOption.Counter) {
            const terminalErrorMsg = validateTerminalValue(
                terminal,
                value,
                direction
            );
            setTerminalValueError(terminalErrorMsg);

            setStepSizeValueError(validateStepSizeValue(step, value, terminal));
        }
    };

    const handleDirection = (value: string) => {
        const directionUpdated = Number(value);
        const initialValue =
            directionUpdated === Direction.Up
                ? String(Number(MIN_TERMINAL_VALUE) - 1)
                : MAX_TERMINAL_VALUE;
        const terminalValue =
            directionUpdated === Direction.Up
                ? MAX_TERMINAL_VALUE
                : MIN_TERMINAL_VALUE;
        setEventVariableValue({
            ...eventVariableValue,
            [eventVariableType]: initialValue,
        });
        setTerminal(terminalValue);
        setDirection(directionUpdated);
        setOverflow(OVERFLOW_DEFAULT);
        setStep(STEP_DEFAULT);
        setEventVariableValueError("");
        setTerminalValueError("");
        setStepSizeValueError("");
    };

    const handleTerminalValue = (value: string) => {
        setTerminal(value);
        const errorMsg = validateTerminalValue(
            value,
            eventVariableValue[eventVariableType],
            direction
        );
        setTerminalValueError(errorMsg);

        setStepSizeValueError(
            validateStepSizeValue(
                step,
                eventVariableValue[eventVariableType],
                value
            )
        );
    };

    const handleStepValue = (value: string) => {
        setStep(value);
        setStepSizeValueError(
            validateStepSizeValue(
                value,
                eventVariableValue[eventVariableType],
                terminal
            )
        );
    };

    const handleOverflowValue = (value: boolean) => {
        setOverflow(value);
    };

    const handleResetCurrentValue = async () => {
        setIsShowResetModal(false);
        const resp = await EventVariableService.resetEventVariableCurrentValue(
            eventVariableId
        );
        if (isHttpSuccess(resp.status)) {
            await fetchData();
            showSuccessAlert({
                message: "Event variable has been reset successfully.",
            });
        } else {
            showErrorAlert(getAPIError(resp, INVALID_OPERATION_MESSAGE));
        }
    };

    const getBoolOptions = () => {
        return [
            {
                key: "true",
                value: "true",
                label: "True",
            },
            {
                key: "false",
                value: "false",
                label: "False",
            },
        ];
    };

    const onChangeLimit = (value: string, type: "upper" | "lower") => {
        if (type === "upper") {
            setUpperLimit(value);
        } else {
            setLowerLimit(value);
        }

        const upperLimitValueString = type === "upper" ? value : upperLimit;
        const lowerLimitValueString = type === "lower" ? value : lowerLimit;
        updateLimitError(
            eventVariableValue[eventVariableType],
            upperLimitValueString,
            lowerLimitValueString
        );
    };

    const getDirectionOptions = () => {
        return Object.entries(Direction)
            .filter(([key]) => !key.isNumeric())
            .map(([key, value]) => ({
                key: key,
                value: String(value),
                label: `${key} count`,
            }));
    };

    return (
        <ContentWrapper
            isPageLoading={isPageLoading}
            isForbiddenResource={isForbiddenResource}
        >
            <div className="page-content">
                <Container fluid>
                    <div>
                        <Row>
                            <Col sm="6">
                                <h5 className="page-title">
                                    {eventVariable.name}
                                </h5>
                            </Col>

                            <Col xs="6" className="text-right">
                                <HoverAuthorizeTooltip permission="event:delete">
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            setIsShowDeleteModal(true);
                                        }}
                                    >
                                        <span>
                                            <img
                                                className="mr-2 ml-2"
                                                src={dltWhiteIcon}
                                                alt="dlt"
                                            />
                                        </span>
                                        {""}
                                        Delete
                                    </Button>
                                </HoverAuthorizeTooltip>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs="12">
                                <Breadcrumb className="w-100">
                                    <Breadcrumb.Item
                                        linkAs={Link}
                                        linkProps={{ to: "/events" }}
                                    >
                                        Event Management
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item
                                        linkAs={Link}
                                        linkProps={{
                                            to: "/events?activeKey=event-variables",
                                        }}
                                    >
                                        Event Variables
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item active>
                                        Event Variable
                                    </Breadcrumb.Item>
                                </Breadcrumb>
                            </Col>
                        </Row>

                        <Row className="mt-2">
                            <Col sm="12">
                                <div className="event-variable-form-header mt-2">
                                    <h5>Event Variable Details</h5>
                                    <div>
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                setIsShowResetModal(true);
                                            }}
                                            className="mr-2"
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={async () => {
                                                await fetchData();
                                                setIsShowEditModal(true);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </div>
                                <div className="form-box event-variable-form-content iot-font-500">
                                    {variableInfo.map((item) => (
                                        <Row key={item.label}>
                                            <Col
                                                sm="2"
                                                className="align-self-center"
                                            >
                                                {item.label}
                                            </Col>
                                            <Col
                                                sm="10"
                                                className={
                                                    "align-self-center " +
                                                    item.className
                                                }
                                            >
                                                {item.value}
                                            </Col>
                                        </Row>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="mt-5 associated-events">
                        <Row>
                            <Col sm="6">
                                <h5 className="page-title">
                                    Associated Events
                                </h5>
                            </Col>
                        </Row>

                        <Row className="cstm-table mt-1">
                            <div className="table-head">
                                <Row className="no-checkbox">
                                    <Col sm="4" className="pl-4">
                                        Event Name
                                    </Col>
                                    <Col sm="3">Event Conditions</Col>
                                    <Col sm="3">Event Actions</Col>
                                    <Col sm="1">Status</Col>
                                    <Col sm="1"></Col>
                                </Row>
                            </div>

                            {renderAssociatedEvents()}
                        </Row>
                    </div>
                </Container>
            </div>

            <DefaultModal
                modalShow={isShowDeleteModal}
                setModalShow={setIsShowDeleteModal}
                modalType={"dlt"}
                modalContent={"Do you want to delete this Event variable?"}
                deleteFunction={handleDeleteEventVariable}
            />

            <DefaultModal
                modalShow={isShowResetModal}
                setModalShow={setIsShowResetModal}
                modalType={"updateConfirm"}
                modalTitle={"Reset Event Variable?"}
                modalContent={
                    <p>
                        This will reset the current value to the initial value.
                        Are you sure <br />
                        you want to reset this Event Variable?
                    </p>
                }
                resendFunction={handleResetCurrentValue}
            />

            <Modal
                centered
                show={isShowEditModal}
                onHide={() => setIsShowEditModal(false)}
                backdrop="static"
                keyboard={false}
                className="border-0"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Edit Event Variable</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center mt-3 mb-3">
                    <Form className="edit-event-variable-form">
                        <Form.Group className="mb-3" controlId={"name"}>
                            <p className="mb-2 edit-form-label">
                                Event Variable Name
                            </p>
                            <Form.Control
                                type="text"
                                required
                                name={"name"}
                                aria-label={"name"}
                                value={eventVariableName}
                                onChange={(e: any) => {
                                    handleEventVariableName(e.target.value);
                                }}
                                isInvalid={
                                    !Patterns.eventVariableNamePattern.test(
                                        eventVariableName
                                    )
                                }
                            ></Form.Control>
                            <Form.Control.Feedback
                                className="text-left mt-2"
                                type="invalid"
                            >
                                {INVALID_EVENT_VARIABLE_NAME_MESSAGE}
                            </Form.Control.Feedback>
                        </Form.Group>

                        {eventVariableType !== TypeOption.Counter && (
                            <Form.Group
                                className="mb-3"
                                controlId={"initial-value"}
                            >
                                <p className="mb-2 edit-form-label">
                                    Initial Value
                                </p>

                                {eventVariableType === TypeOption.Boolean ? (
                                    <Form.Control
                                        className="text-primary-blue-3 bg-light-gray"
                                        as="select"
                                        required
                                        aria-label={"initial-value"}
                                        name={"initial-value"}
                                        value={
                                            eventVariableValue[
                                            eventVariableType
                                            ]
                                        }
                                        onChange={(e) =>
                                            handleEventVariableValue(
                                                e.target.value
                                            )
                                        }
                                        custom
                                    >
                                        {getBoolOptions().map((option) => (
                                            <option
                                                value={option.value}
                                                key={option.key}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                ) : (
                                    <>
                                        <Form.Control
                                            type="text"
                                            required
                                            name={"initial-value"}
                                            aria-label={"initial-value"}
                                            value={
                                                eventVariableValue[
                                                eventVariableType
                                                ]
                                            }
                                            onChange={(e) =>
                                                handleEventVariableValue(
                                                    e.target.value
                                                )
                                            }
                                            isInvalid={
                                                !!eventVariableValueError
                                            }
                                        ></Form.Control>
                                        <Form.Control.Feedback
                                            className="text-left mt-2"
                                            type="invalid"
                                        >
                                            {eventVariableValueError}
                                        </Form.Control.Feedback>
                                    </>
                                )}
                            </Form.Group>
                        )}

                        {[TypeOption.Integer, TypeOption.Float].includes(
                            eventVariableType
                        ) && (
                                <>
                                    <Form.Group
                                        className="mb-3"
                                        controlId="upper-limit"
                                    >
                                        <p className="mb-2 edit-form-label">
                                            Upper Limit
                                        </p>
                                        <Form.Control
                                            type="text"
                                            required
                                            name="upper-limit"
                                            aria-label="upper-limit"
                                            value={upperLimit}
                                            onChange={(e) =>
                                                onChangeLimit(
                                                    e.target.value,
                                                    "upper"
                                                )
                                            }
                                            isInvalid={!!upperLimitError}
                                        />
                                        <Form.Text className="wrap-around-hint text-left mt-2">
                                            The highest value this variable can
                                            reach. Any value exceeding this limit
                                            will be capped at this maximum.
                                        </Form.Text>
                                        <Form.Control.Feedback
                                            type="invalid"
                                            className="text-left mt-2"
                                        >
                                            {upperLimitError}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group
                                        className="mb-3"
                                        controlId="lower-limit"
                                    >
                                        <p className="mb-2 edit-form-label">
                                            Lower Limit
                                        </p>
                                        <Form.Control
                                            type="text"
                                            required
                                            name="lower-limit"
                                            aria-label="lower-limit"
                                            value={lowerLimit}
                                            onChange={(e) =>
                                                onChangeLimit(
                                                    e.target.value,
                                                    "lower"
                                                )
                                            }
                                            isInvalid={!!lowerLimitError}
                                        />
                                        <Form.Text className="wrap-around-hint text-left mt-2">
                                            The lowest value this variable can
                                            reach. Any value below this limit will
                                            be capped at this minimum.
                                        </Form.Text>
                                        <Form.Control.Feedback
                                            type="invalid"
                                            className="text-left mt-2"
                                        >
                                            {lowerLimitError}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </>
                            )}

                        {eventVariableType === TypeOption.Counter && (
                            <>
                                <Form.Group
                                    className="mb-3"
                                    controlId="direction"
                                >
                                    <p className="mb-2 edit-form-label">
                                        Count
                                    </p>
                                    <Form.Control
                                        className="text-primary-blue-3 bg-light-gray"
                                        as="select"
                                        required
                                        aria-label={"direction"}
                                        name={"direction"}
                                        value={String(direction)}
                                        onChange={(e: any) =>
                                            handleDirection(e.target.value)
                                        }
                                        custom
                                    >
                                        {getDirectionOptions().map((option) => (
                                            <option
                                                value={option.value}
                                                key={option.key}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId={"initial-value"}
                                >
                                    <p className="mb-2 edit-form-label">
                                        Initial Value
                                    </p>

                                    <>
                                        <Form.Control
                                            type="text"
                                            required
                                            name={"initial-value"}
                                            aria-label={"initial-value"}
                                            value={
                                                eventVariableValue[
                                                eventVariableType
                                                ]
                                            }
                                            onChange={(e) =>
                                                handleEventVariableValue(
                                                    e.target.value
                                                )
                                            }
                                            isInvalid={
                                                !!eventVariableValueError
                                            }
                                        ></Form.Control>
                                        <Form.Control.Feedback
                                            className="text-left mt-2"
                                            type="invalid"
                                        >
                                            {eventVariableValueError}
                                        </Form.Control.Feedback>
                                    </>
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId="terminal"
                                >
                                    <p className="mb-2 edit-form-label">
                                        Terminal Value
                                    </p>
                                    <Form.Control
                                        type="text"
                                        required
                                        name="terminal"
                                        aria-label="terminal"
                                        value={terminal}
                                        onChange={(e: any) =>
                                            handleTerminalValue(e.target.value)
                                        }
                                        isInvalid={!!terminalValueError}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        className="text-left mt-2"
                                    >
                                        {terminalValueError}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="step">
                                    <p className="mb-2 edit-form-label">
                                        Step size
                                    </p>
                                    <Form.Control
                                        type="text"
                                        required
                                        name="step"
                                        aria-label="step"
                                        value={step}
                                        onChange={(e) =>
                                            handleStepValue(e.target.value)
                                        }
                                        isInvalid={!!stepSizeValueError}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        className="text-left mt-2"
                                    >
                                        {stepSizeValueError}
                                    </Form.Control.Feedback>
                                    <Form.Text className="wrap-around-hint text-left mt-2">
                                        The absolute difference between the
                                        Initial Value and Terminal Value must be
                                        a multiple of Step size.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId="overflow"
                                >
                                    <div className="mb-2 edit-form-label edit-variable-wraparound">
                                        <span className="mr-2">Wraparound</span>
                                        <Form.Check
                                            type="switch"
                                            name="overflow"
                                            checked={overflow}
                                            onChange={(e) =>
                                                handleOverflowValue(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </div>

                                    <Form.Text className="wrap-around-hint text-left mt-2">
                                        (If enabled : Terminal value + Step size
                                        = Initial Value, if disabled: Terminal
                                        value + Step size = Terminal value)
                                    </Form.Text>
                                </Form.Group>
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={(e) => {
                            setIsShowEditModal(false);
                            updateEventVariable(e);
                        }}
                        disabled={
                            !Patterns.eventVariableNamePattern.test(
                                eventVariableName
                            ) ||
                            !!eventVariableValueError ||
                            !!upperLimitError ||
                            !!lowerLimitError ||
                            !!terminalValueError ||
                            !!stepSizeValueError ||
                            isUpdating
                        }
                    >
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </ContentWrapper>
    );
};

export default EventVariableDetail;
